import React, { PropsWithChildren } from "react";
import { cn } from "@/lib/utils";
import Link from "next/link";
import { CaretRight } from "./svg-icons";
import Image from "next/image";

function BoxSmall({ children, className, ...props }: PropsWithChildren<{ className?: string }>) {
  return (
    <article
      className={cn(
        "h-[361px] w-full rounded-lg bg-midnightIndigo-100 pb-[1.88rem] sm:h-[370px] md:h-[278px]",
        className
      )}
      {...props}
    >
      {children}
    </article>
  );
}

function BoxMedium({ children, className, ...props }: PropsWithChildren<{ className?: string }>) {
  return (
    <article
      className={cn(
        "h-[361px] w-full rounded-lg bg-midnightIndigo-100 pb-[1.88rem] sm:h-[370px] md:h-[448px]",
        className
      )}
      {...props}
    >
      {children}
    </article>
  );
}

function BoxLarge({ children, className, ...props }: PropsWithChildren<{ className?: string }>) {
  return (
    <article
      className={cn("h-[361px] w-full rounded-lg sm:h-[370px] md:h-[580px]", className)}
      {...props}
    >
      {children}
    </article>
  );
}

function BoxTitle({ children }: { children?: React.ReactNode }) {
  return (
    <p className="mb-2 text-[1.5rem] font-medium leading-none text-midnightIndigo-400 sm:text-[1.625rem] lg:mb-4 lg:text-[2rem]">
      {children}
    </p>
  );
}

function BoxDescription({ children }: { children?: React.ReactNode }) {
  return <p className="text-[0.9375rem] leading-[1.3125rem] text-midnightIndigo-350">{children}</p>;
}

interface IBoxProps {
  src: string;
  alt: string;
  title: string;
  description: string;
  link?: React.ReactNode;
  className?: string;
}
function BoxOne({ src, alt, title, description, className = "", link = null }: IBoxProps) {
  return (
    <BoxSmall className={cn("bg-cover bg-no-repeat", className)}>
      <div className="flex h-full flex-col">
        <div className="relative mt-auto h-[9.125rem] w-full">
          <Image src={src} alt={alt} fill className="object-contain" priority />
        </div>

        <div className="mt-auto flex bg-top bg-no-repeat">
          <div className="mt-auto px-[1.88rem] text-left">
            <BoxTitle>{title}</BoxTitle>
            <BoxDescription>{description}</BoxDescription>
            {link}
          </div>
        </div>
      </div>
    </BoxSmall>
  );
}

function BoxTwo({ src, alt, title, description, link = null, className = "" }: IBoxProps) {
  return (
    <BoxMedium className={className}>
      <div className="flex h-full flex-col">
        <div className="relative mt-auto h-full w-full">
          <Image src={src} alt={alt} fill className="object-contain" priority />
        </div>
        <div className="mt-auto flex bg-top bg-no-repeat">
          <div className="mt-auto px-[1.88rem] text-left">
            <BoxTitle>{title}</BoxTitle>
            <BoxDescription>{description}</BoxDescription>
            {link}
          </div>
        </div>
      </div>
    </BoxMedium>
  );
}
function FedEXBox({ src = "", className = "" }: Partial<IBoxProps>) {
  return (
    <BoxSmall
      className={cn("bg-system-grey bg-cover bg-right-bottom bg-no-repeat !pb-0", className)}
    >
      <div className="flex h-full">
        <div className="mt-auto w-full max-w-[190px] justify-end pb-[1.88rem] pl-[1.88rem] text-left">
          <BoxTitle>
            FedEx <br />
            Express
          </BoxTitle>
          <BoxDescription>Delivered to you, anywhere in the world, within 3 days.</BoxDescription>
        </div>
        <div className="relative h-full w-full">
          <Image
            src={src}
            alt="Bulk ordering made easy"
            fill
            className="h-full w-full object-cover"
            priority
          />
        </div>
      </div>
    </BoxSmall>
  );
}

export { BoxSmall, BoxTwo, BoxOne, BoxMedium, BoxLarge, BoxTitle, BoxDescription, FedEXBox };
